/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import { MainNavigationMenuItem } from '../../main-navigation';
import MainNavigationSmallDropdown from '../main-navigation-small-dropdown/main-navigation-small-dropdown';
import MainNavigationSmallMenuItem from '../main-navigation-small-menu-item/main-navigation-small-menu-item';
import styles from './main-navigation-small-menu-item-list.module.scss';

export interface MainNavigationSmallMenuItemListProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether this is a featured item list */
  isFeatured?: boolean;
  /** Indicates whether this is a priority item list */
  isPriority?: boolean;
  /** An array of links to show */
  items?: MainNavigationMenuItem[];
  /** An event that is triggered on close */
  onClose?: () => void;
  /** An event that is triggered on open */
  onOpen?: () => void;
}

const MainNavigationSmallMenuItemList = ({
  className,
  isFeatured = false,
  isPriority = false,
  items = [],
  onClose,
  onOpen,
}: MainNavigationSmallMenuItemListProps) => {
  const [currentDropdown, setCurrentDropdown] =
    useState<MainNavigationMenuItem>();
  const componentRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);

  useEffect(() => {
    if (isPriority) return;

    const handleResize = () => {
      if (!componentRef.current) return;

      if (!itemsRef.current) return;

      const componentWidth = componentRef.current.offsetWidth;
      const itemsWidth = itemsRef.current.offsetWidth;

      setHasOverflowRight(itemsWidth > componentWidth);
      setHasOverflowLeft(false);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [items]);

  const handleClick = (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    item?: MainNavigationMenuItem,
  ) => {
    if (!item?.items || item.items?.length === 0) {
      setCurrentDropdown(undefined);
      onClose && onClose();

      return;
    }

    event?.preventDefault();

    if (currentDropdown === item) {
      setCurrentDropdown(undefined);
      onClose && onClose();
    } else {
      setCurrentDropdown(item);
      onOpen && onOpen();
    }
  };

  const handleKeyup = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setCurrentDropdown(undefined);
      onClose && onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, []);

  return (
    <div
      ref={componentRef}
      className={classNames(styles.component, className, {
        [styles.hasOverflowLeft]: hasOverflowLeft,
        [styles.hasOverflowRight]: hasOverflowRight,
      })}
    >
      {hasOverflowLeft && (
        // TODO: implement scroll/swipe functionality when clicking/swiping
        <button className={styles.previousButton}>
          <Icon name="ChevronLeft" size={12} />
        </button>
      )}

      <div ref={itemsRef} className={styles.items}>
        {items.map((item, itemIndex) => (
          <React.Fragment key={itemIndex}>
            <MainNavigationSmallMenuItem
              {...item}
              isActive={item === currentDropdown}
              isFeatured={isFeatured || item.isFeatured}
              isPriority={isPriority || item.isPriority}
              onClick={
                item.items && item.items.length > 0
                  ? event => event && handleClick(event, item)
                  : undefined
              }
            />

            {item.items && item.items.length > 0 && (
              <MainNavigationSmallDropdown
                isOpen={item === currentDropdown}
                items={item.items}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {hasOverflowRight && (
        // TODO: implement scroll/swipe functionality when clicking/swiping
        <button className={styles.nextButton}>
          <Icon name="ChevronRight" size={12} />
        </button>
      )}
    </div>
  );
};

MainNavigationSmallMenuItemList.displayName = 'MainNavigationSmallMenuItemList';

export default MainNavigationSmallMenuItemList;
